import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { chain, difference, map } from 'lodash';
import { settlementAPI } from 'redux/settlementAPI';
import { availablePageSizes } from 'config';
import { createPaginationAdapter } from '@payer-admin-ui/components-v2/src/state/paginationAdapter';
import { createFilterAdapter } from '@payer-admin-ui/components-v2/src/state/filterAdapter';
import { patch } from '@payer-admin-ui/components-v2/src/functions/lodashExtensions';

export const fetchSettlement = createAsyncThunk('settlements/fetchById', async ({ id }) => {
  return settlementAPI.fetchById({ id });
});

export const fetchSettlements = createAsyncThunk('settlements/fetchPage', async (_, { getState }) => {
  const { page, pageSize, appliedFilters } = getState().settlements.listView;
  return settlementAPI.fetchPage({ page, pageSize, filters: appliedFilters });
});

const SINGLE_CACHE_SIZE = 10;

const emptyFilters = {
  currencyCode: [],
  payoutDateStart: '',
  payoutDateEnd: '',
  bookingDateStart: '',
  bookingDateEnd: '',
  search: '',
};

const entityAdapter = createEntityAdapter();
const paginationAdapter = createPaginationAdapter({
  selectState: state => state.listView,
});
const filterAdapter = createFilterAdapter({
  selectState: state => state.listView,
  emptyFilters,
  reset: paginationAdapter.reset,
});

const initialFilters = {
  ...emptyFilters,
};

const initialState = entityAdapter.getInitialState({
  singleView: {
    cachedIds: [],
  },
  listView: {
    isFetching: false,
    error: null,
    totalCount: 0,
    ids: [],
    page: 0, // zero-indexed in state, one-indexed in UI
    pageSize: availablePageSizes[0],
    filters: initialFilters,
    appliedFilters: initialFilters,
  },
});

entityAdapter.pruneCache = state => {
  entityAdapter.removeMany(state, difference(state.ids, state.singleView.cachedIds, state.listView.ids));
};

entityAdapter.upsertOnePruneCache = (state, entity) => {
  entityAdapter.upsertOne(state, entity);
  state.singleView.cachedIds = chain([entity.id]).union(state.singleView.cachedIds).take(SINGLE_CACHE_SIZE).value();
  entityAdapter.pruneCache(state);
};

const settlementsSlice = createSlice({
  name: 'settlements',
  initialState,
  reducers: {
    resetError: state => {
      state.listView.error = null;
    },
    setPage: paginationAdapter.setPage,
    setPageSize: paginationAdapter.setPageSize,
    setFilter: filterAdapter.setFilter,
    clearAllFilters: filterAdapter.clearAllFilters,
    applyFilters: filterAdapter.applyFilters,
  },
  extraReducers: builder => {
    builder.addCase(fetchSettlement.pending, (state, { meta: { arg } }) => {
      entityAdapter.upsertOnePruneCache(state, { id: arg.id, isFetching: true, error: null });
    });
    builder.addCase(fetchSettlement.fulfilled, (state, { meta: { arg }, payload: settlement }) => {
      entityAdapter.upsertOne(state, { id: arg.id, settlement, isFetching: false, error: null });
    });
    builder.addCase(fetchSettlement.rejected, (state, { meta: { arg }, error }) => {
      entityAdapter.upsertOne(state, { id: arg.id, settlement: undefined, isFetching: false, error });
    });

    builder.addCase(fetchSettlements.pending, (state, _) => {
      patch(state.listView, {
        isFetching: true,
        error: null,
      });
    });
    builder.addCase(fetchSettlements.fulfilled, (state, { payload: { contents, totalCount } }) => {
      const settlements = normalize(contents);
      patch(state.listView, {
        ids: map(settlements, 'id'),
        totalCount,
        isFetching: false,
      });
      entityAdapter.upsertMany(state, settlements);
      entityAdapter.pruneCache(state);
    });
    builder.addCase(fetchSettlements.rejected, (state, { error }) => {
      patch(state.listView, {
        ids: [],
        totalCount: null,
        isFetching: false,
        error,
      });
      entityAdapter.pruneCache(state);
    });
  },
});

const normalize = settlements =>
  map(settlements, settlement => ({ id: settlement.payoutReference, settlement, isFetching: false, error: null }));

export const {
  actions: { resetError, setPage, setPageSize, setFilter, clearAllFilters, applyFilters },
  reducer: settlementsReducer,
} = settlementsSlice;

export const settlementsSelectors = entityAdapter.getSelectors(state => state.settlements);
